import clsx from "clsx";
import { useState, type MouseEvent } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { isMobileViewport } from "../hooks/useIsMobileViewport.ts";
import type { View } from "../../../../common/types.ts";
import { selectScheduleMobileWindow } from "./gamesTemplate.ts";
import { toWorker } from "../util/toWorker.ts";
import { useLocalPartial } from "../util/local.ts";
import { NoGamesMessage } from "./GameLog.tsx";
import { ScoreBox } from "../components/ScoreBox/index.tsx";
import MobileListToggle from "../components/MobileListToggle.tsx";
import { TeamLogoInline } from "../components/TeamLogoInline.tsx";
import { TeamScoreValue } from "../components/TeamScoreValue.tsx";
import i18n from "../util/i18n.ts";
import { translateTeamName, translateTeamRegion } from "../util/teamNames.ts";
import { MobileContentHero } from "../mobile/mobilePrimitives.tsx";
import { helpers } from "../util/helpers.ts";
import { createMobileReturnUrl } from "../util/mobilePageContext.ts";
import getWinner from "../../../../common/getWinner.ts";
import {
  getDailyScheduleActionPlan,
  getSimToDayTargetGid,
  isDailyScheduleUserGame,
  sortDailyScheduleUpcomingGames,
} from "./dailySchedulePriority.ts";

const isTeamGame = (game: { teams: { tid: number }[] }, userTid: number) =>
  game.teams.some((team) => team.tid === userTid);

// N3-D: completed games collapse from one full ScoreBox card each into a compact
// score row (both teams + score + winner marker). The whole row is the box-score
// link, seeded with a return (m_return) so the back control climbs precisely back
// to this matchday (N2-2 tab-preservation, kept intact).
const CompletedGameRow = ({
  game,
  returnHref,
  returnLabel,
  teamInfoCache,
  userTid,
}: {
  game: View<"dailySchedule">["completed"][number];
  returnHref: string;
  returnLabel: string;
  teamInfoCache: Record<
    number,
    { abbrev: string; region: string; name: string; imgURL?: string; imgURLSmall?: string }
  >;
  userTid: number;
}) => {
  const winner = getWinner(game.teams);
  const gameSeason = game.season;
  const branding0 = game.teams[0].branding ?? teamInfoCache[game.teams[0].tid];
  const boxScoreHref = helpers.leagueUrl([
    "game_log",
    `${branding0?.abbrev ?? ""}_${game.teams[0].tid}`,
    gameSeason,
    game.gid,
  ]);
  const seedCompletedGameReturn = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      typeof window === "undefined" ||
      !isMobileViewport() ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.currentTarget.href = createMobileReturnUrl(boxScoreHref, {
      captureCurrentReturnUrl: false,
      pageID: "dailySchedule",
      returnHref,
      returnLabel,
      scrollY: window.scrollY,
      sourcePage: "dailySchedule",
    });
  };

  return (
    <a
      className="daily-schedule-completed-row"
      data-daily-schedule-completed-row=""
      data-gid={game.gid}
      href={boxScoreHref}
      onClick={seedCompletedGameReturn}
      data-box-score-return-seed="click"
    >
      {[0, 1].map((i) => {
        const t = game.teams[i]!;
        const branding = t.branding ?? teamInfoCache[t.tid];
        const isWinner = winner === i;
        const teamName = branding
          ? `${translateTeamRegion(branding.region)} ${translateTeamName(branding.name)}`
          : String(t.tid);

        return (
          <div
            className={clsx("daily-schedule-completed-team", {
              "daily-schedule-completed-team-winner": isWinner,
              "daily-schedule-completed-team-user": t.tid === userTid,
            })}
            data-winner={isWinner ? "" : undefined}
            key={i}
          >
            <TeamLogoInline
              imgURL={branding?.imgURL}
              imgURLSmall={branding?.imgURLSmall}
              includePlaceholderIfNoLogo
              size={40}
            />
            <span className="daily-schedule-completed-name text-truncate">{teamName}</span>
            <span className="daily-schedule-completed-score">
              <TeamScoreValue pts={t.pts} sPts={t.sPts} />
            </span>
            <span aria-hidden={!isWinner} className="daily-schedule-completed-winner">
              {isWinner ? (
                <svg height={12} viewBox="0 0 50 100" width={6} xmlns="http://www.w3.org/2000/svg">
                  <polygon points="0,50 50,0 50,100 0,50" />
                </svg>
              ) : null}
            </span>
          </div>
        );
      })}
      <span className="visually-hidden">
        {i18n.t("scoreBox.boxScore", { ns: "ui", defaultValue: "Box score" })}
      </span>
    </a>
  );
};

// M3-2: the standalone /schedule page is retired and its full-season content is
// absorbed here as the dailySchedule "整季视图" (reached via
// /daily_schedule/team/:abbrev). This is the former Schedule.tsx body, minus its
// own TitleBar (the parent DailySchedule owns the title bar for the season view)
// and minus the dead m_return query parsing (nothing seeded a schedule return
// state). The readonly-opponent hero + "返回本队赛程" link are preserved so the
// "他队赛程可达" fact stays true, now pointing at the merged team route.
const SeasonScheduleView = ({
  abbrev,
  completed,
  tid,
  topPlayers,
  upcoming,
  userTid,
}: NonNullable<View<"dailySchedule">["seasonView"]> & { userTid: number }) => {
  const mobileWindow = selectScheduleMobileWindow({
    completed,
    completedLimit: 5,
    upcoming,
    upcomingLimit: 5,
  });

  const nextGame = mobileWindow.nextGame;
  const upcomingAfterNext = mobileWindow.upcoming.slice(nextGame ? 1 : 0);
  const getOpponentTid = (game: (typeof mobileWindow.upcoming)[number]) =>
    game.teams[0].tid === tid ? game.teams[1].tid : game.teams[0].tid;

  return (
    <div data-mobile-schedule-template data-season-schedule-planning-page="">
      <div
        aria-label={i18n.t("schedule.returnToTeam", {
          ns: "ui",
          defaultValue: "返回球队",
        })}
      >
        <a
          className="btn btn-link btn-sm px-0"
          data-team-context-source-return=""
          href={helpers.leagueUrl(["daily_schedule", "team", String(userTid)])}
        >
          {i18n.t("schedule.p2s02ReturnMySchedule", {
            ns: "ui",
            defaultValue: "返回本队赛程",
          })}
        </a>
      </div>
      <MobileContentHero
        aria-label={i18n.t("schedule.p2s02ReadonlyOpponentSchedule", {
          ns: "ui",
          defaultValue: "只读对手赛程",
        })}
        action={
          <a
            className="btn btn-primary"
            data-schedule-primary-action=""
            data-schedule-matchday-link=""
            href={helpers.leagueUrl(["daily_schedule"])}
          >
            {i18n.t("schedule.openMatchday", {
              ns: "ui",
              defaultValue: "Open matchday",
            })}
          </a>
        }
        className="mb-3"
        data-mobile-schedule-focus=""
        data-next-game-hero=""
        data-v5-schedule-next-game-first-screen=""
      >
        {nextGame ? (
          <ul className="list-group mb-3" data-schedule-next-game-card="">
            <ScoreBox
              className="mb-0"
              scheduleLayout
              game={{
                // Leave out forceTie, since ScoreBox wants the value for finished games
                finals: nextGame.finals,
                gid: nextGame.gid,
                season: nextGame.season,
                teams: nextGame.teams,
              }}
              playersUpcoming={topPlayers.playersByTid[getOpponentTid(nextGame)]}
              playersUpcomingAbbrev
            />
          </ul>
        ) : null}
        <div className="d-flex flex-wrap" data-five-game-window="" style={{ gap: "2rem" }}>
          <div
            style={{ maxWidth: 510 }}
            className="flex-grow-1"
            data-mobile-schedule-section="completed"
          >
            <h2 data-recent-results-summary="">
              {i18n.t("schedule.recentResultsWindow", {
                ns: "ui",
                defaultValue: "Recent results",
              })}
            </h2>
            {mobileWindow.completed.map((game) => (
              <ScoreBox
                boxScoreTeamOverride={`${abbrev}_${tid}`}
                className="mb-3"
                scheduleLayout
                key={game.gid}
                game={game}
              />
            ))}
          </div>
          {upcomingAfterNext.length > 0 ? (
            <div
              style={{ maxWidth: 510 }}
              className="flex-grow-1"
              data-mobile-schedule-section="upcoming"
            >
              <h2>
                {i18n.t("schedule.upcomingWindow", {
                  ns: "ui",
                  defaultValue: "Next five games",
                })}
              </h2>
              <ul className="list-group">
                {upcomingAfterNext.map((game) => (
                  <ScoreBox
                    key={game.gid}
                    className="mb-3"
                    scheduleLayout
                    game={{
                      finals: game.finals,
                      gid: game.gid,
                      season: game.season,
                      teams: game.teams,
                    }}
                    playersUpcoming={topPlayers.playersByTid[getOpponentTid(game)]}
                    playersUpcomingAbbrev
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </MobileContentHero>
    </div>
  );
};

const DailySchedule = ({
  completed,
  currentSeason,
  day,
  days,
  elam,
  elamASG,
  isToday,
  phase,
  season,
  seasonView,
  ties,
  topPlayers,
  upcoming,
  userTid,
}: View<"dailySchedule">) => {
  const isSeasonView = seasonView !== undefined;
  const isHistoricalSeason = season !== currentSeason;
  const dailyScheduleTitle = isHistoricalSeason
    ? i18n.t("dailySchedule.historyTitle", {
        ns: "ui",
        season,
        defaultValue: "{{season}} matchday archive",
      })
    : i18n.t("dailySchedule.title", {
        ns: "ui",
        defaultValue: "Matchday",
      });

  // Season/day selection lives on the single TitleBar dropdown. Conference
  // range is intentionally absent: a matchday always shows the whole league.
  // N3-C: in the "整季视图" (season view) the title bar switches to the team
  // schedule dropdown so team switching works for the merged season schedule.
  useTitleBar(
    isSeasonView
      ? {
          title: i18n.t("schedule.title", {
            ns: "ui",
            defaultValue: "Season Schedule",
          }),
          dropdownView: "daily_schedule",
          dropdownFields: { teams: seasonView.abbrev },
          // M3-2: the standalone /schedule route is gone, so switching teams in
          // the season "整季视图" dropdown targets the merged team route instead
          // of building the retired ["schedule", abbrev] URL.
          dropdownCustomURL: (fields: Record<string, number | string>) =>
            helpers.leagueUrl(["daily_schedule", "team", String(fields.teams)]),
        }
      : {
          title: dailyScheduleTitle,
          dropdownView: "daily_schedule",
          dropdownFields: { seasons: season, days: day },
          dropdownCustomOptions: {
            days,
          },
        },
  );

  // N3-C: the season "整季视图" is merged into the dailySchedule surface. This
  // tab strip switches between the per-day matchday view and the full-season
  // (team) schedule view; the standalone /schedule route redirects here.
  const scheduleViewTabs = (
    <nav
      aria-label={i18n.t("dailySchedule.viewSwitcher", {
        ns: "ui",
        defaultValue: "Schedule view",
      })}
      className="d-flex flex-wrap gap-2 mb-3"
      data-daily-schedule-view-tabs=""
    >
      <a
        aria-current={!isSeasonView ? "page" : undefined}
        className={clsx("btn btn-sm", !isSeasonView ? "btn-primary" : "btn-light-bordered")}
        data-daily-schedule-view-tab="matchday"
        href={helpers.leagueUrl(["daily_schedule"])}
      >
        {i18n.t("dailySchedule.viewTabMatchday", {
          ns: "ui",
          defaultValue: "Matchday",
        })}
      </a>
      <a
        aria-current={isSeasonView ? "page" : undefined}
        className={clsx("btn btn-sm", isSeasonView ? "btn-primary" : "btn-light-bordered")}
        data-daily-schedule-view-tab="season"
        href={helpers.leagueUrl([
          "daily_schedule",
          "team",
          isSeasonView ? `${seasonView.abbrev}_${seasonView.tid}` : String(userTid),
        ])}
      >
        {i18n.t("dailySchedule.viewTabSeason", {
          ns: "ui",
          defaultValue: "Full season",
        })}
      </a>
    </nav>
  );

  const { gameSimInProgress, teamInfoCache } = useLocalPartial([
    "gameSimInProgress",
    "teamInfoCache",
  ]);
  const [otherGamesExpanded, setOtherGamesExpanded] = useState(false);

  // Rendered after every hook so the season "整季视图" can early-return without
  // breaking the rules of hooks. The embedded Schedule view carries the
  // opponent-schedule return semantics (g6) via the mobile return state query.
  if (isSeasonView) {
    return (
      <div data-games-root-task-page="" data-daily-schedule-season-view="">
        {scheduleViewTabs}
        <SeasonScheduleView {...seasonView} userTid={userTid} />
      </div>
    );
  }

  const allowSimulationActions = !isHistoricalSeason;

  const upcomingAndCompleted = upcoming.length > 0 && completed.length > 0;

  const tradeDeadline =
    upcoming.length === 1 && upcoming[0]!.teams[0].tid === -3 && upcoming[0]!.teams[1].tid === -3;
  const upcomingSorted = sortDailyScheduleUpcomingGames(upcoming, userTid);
  const primaryGame =
    upcomingSorted.find((game) => isDailyScheduleUserGame(game, userTid)) ?? upcomingSorted[0];
  const primaryGameUserTeam =
    primaryGame !== undefined ? isDailyScheduleUserGame(primaryGame, userTid) : false;
  const primaryGameActions = primaryGame
    ? getDailyScheduleActionPlan({
        isToday,
        tradeDeadline,
        userTeamGame: primaryGameUserTeam,
      })
    : undefined;

  let primaryPlayersUpcoming: [any, any] | undefined;
  if (primaryGame) {
    const x0 = topPlayers.playersByTid[primaryGame.teams[0].tid];
    const x1 = topPlayers.playersByTid[primaryGame.teams[1].tid];

    // Undefined for ASG
    if (x0 && x1) {
      primaryPlayersUpcoming = [x0[0], x1[0]];
    }
  }

  const primaryAction = !allowSimulationActions ? null : tradeDeadline && isToday && primaryGame ? (
    <button
      className="btn btn-primary"
      data-daily-schedule-action-kind="advance-trade-deadline"
      data-daily-schedule-primary-action=""
      data-daily-schedule-trade-deadline-advance=""
      disabled={gameSimInProgress}
      onClick={() => toWorker("actions", "simGame", primaryGame.gid)}
      type="button"
    >
      {i18n.t("dailySchedule.simPastDeadlineAction", {
        ns: "ui",
        defaultValue: "Sim one day",
      })}
    </button>
  ) : primaryGame && primaryGameActions ? (
    <div className="d-flex flex-wrap gap-2">
      {primaryGameActions.map((action) => (
        <button
          className={`btn ${action.primary ? "btn-primary" : "btn-secondary"}`}
          data-daily-schedule-action-kind={action.kind}
          data-daily-schedule-primary-action={action.primary ? "" : undefined}
          data-daily-schedule-secondary-action={action.primary ? undefined : ""}
          disabled={gameSimInProgress}
          key={action.kind}
          onClick={() =>
            toWorker("actions", action.kind === "watch" ? "liveGame" : "simGame", primaryGame.gid)
          }
          type="button"
        >
          {action.kind === "watch"
            ? i18n.t("dailySchedule.watchGameAction", {
                ns: "ui",
                defaultValue: "Watch game",
              })
            : i18n.t("dailySchedule.simGameAction", {
                ns: "ui",
                defaultValue: "Sim game",
              })}
        </button>
      ))}
    </div>
  ) : upcoming.length > 0 && !isToday ? (
    <button
      className="btn btn-primary"
      data-daily-schedule-primary-action=""
      data-daily-schedule-sim-to-day=""
      disabled={gameSimInProgress}
      onClick={() => {
        const minGid = getSimToDayTargetGid(upcoming);
        if (minGid !== undefined) {
          toWorker("actions", "simToGame", minGid);
        }
      }}
      type="button"
    >
      {i18n.t("dailySchedule.simToDay", {
        ns: "ui",
        defaultValue: "Sim to day",
      })}
    </button>
  ) : null;
  const secondaryUpcomingGames =
    primaryGameUserTeam && primaryGame !== undefined
      ? upcomingSorted.filter((game) => game.gid !== primaryGame.gid)
      : upcomingSorted;
  const showSecondaryUpcomingGames = secondaryUpcomingGames.length <= 1 || otherGamesExpanded;
  const completedSorted = isHistoricalSeason
    ? [...completed].sort((a, b) => Number(isTeamGame(b, userTid)) - Number(isTeamGame(a, userTid)))
    : completed;

  const readonlyIndicator = isHistoricalSeason ? (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span className="badge text-bg-secondary">
        {i18n.t("teamContextBar.readonly", {
          ns: "ui",
          defaultValue: "View only",
        })}
      </span>
      <a className="btn btn-outline-secondary btn-sm" href={helpers.leagueUrl(["daily_schedule"])}>
        {i18n.t("dailySchedule.returnCurrentSeason", {
          ns: "ui",
          defaultValue: "Return to current season",
        })}
      </a>
    </div>
  ) : null;

  // R3-1: the former "选择日期" (Choose date) disclosure sheet — a day-picker
  // select plus a "查看整季赛程" link — is removed. Both are now fully covered by
  // the single title-bar day chip (M5-2 dropdownFields.days) and the "整季视图"
  // tab (N3-C scheduleViewTabs), so the on-page control was a duplicate date
  // language (法则二: same function, one site-wide control).

  return (
    <div
      data-games-root-task-page=""
      data-host-state={days.length === 0 ? "no-games" : "ready"}
      data-host-state-host="dailySchedule"
      data-host-state-row-count={days.length}
      data-matchday-task-page=""
      data-v5-daily-schedule-template=""
    >
      {scheduleViewTabs}
      {days.length === 0 ? (
        <MobileContentHero
          className="mb-3"
          data-matchday-hero=""
          data-rest-day-next-game="true"
          data-v5-daily-next-game-first-screen=""
        >
          <div data-matchday-empty-state="">
            <NoGamesMessage warnAboutDelete={season < currentSeason} />
          </div>
        </MobileContentHero>
      ) : (
        <>
          <MobileContentHero
            action={primaryAction}
            className="mb-3"
            data-matchday-hero=""
            data-my-game-hero-model=""
            data-rest-day-next-game={primaryGame ? "false" : "true"}
            data-v5-daily-next-game-first-screen=""
          >
            {primaryGame ? (
              <div className="daily-schedule-game-card" data-v5-daily-matchup-card="">
                <ScoreBox
                  className="mb-0"
                  scheduleLayout
                  game={{
                    // Leave out forceTie, since ScoreBox wants the value for finished games
                    finals: primaryGame.finals,
                    gid: primaryGame.gid,
                    season: primaryGame.season,
                    teams: primaryGame.teams,
                  }}
                  playersUpcoming={primaryPlayersUpcoming}
                />
              </div>
            ) : null}
            {readonlyIndicator}
          </MobileContentHero>

          {tradeDeadline ? (
            <p data-daily-schedule-trade-deadline-explanation="">
              {i18n.t("dailySchedule.simPastDeadline", {
                ns: "ui",
                defaultValue:
                  "Sim one day to move past the trade deadline, and then the next day's games will be available here.",
              })}
            </p>
          ) : null}

          {secondaryUpcomingGames.length > 0 ? (
            <div
              className="mb-3"
              data-other-games-secondary=""
              data-expanded={showSecondaryUpcomingGames ? "true" : "false"}
            >
              {showSecondaryUpcomingGames ? (
                <>
                  {upcomingAndCompleted ? (
                    <h2 className="mt-3">
                      {i18n.t("dailySchedule.matchdayUpcoming", {
                        ns: "ui",
                        defaultValue: "Games to play",
                      })}
                    </h2>
                  ) : null}
                  <div className="daily-schedule-upcoming-list d-flex flex-wrap mt-3">
                    {secondaryUpcomingGames.map((game) => {
                      const userTeamGame = isDailyScheduleUserGame(game, userTid);
                      const actionPlan = allowSimulationActions
                        ? getDailyScheduleActionPlan({
                            isToday,
                            tradeDeadline,
                            userTeamGame,
                          })
                        : undefined;
                      const primaryGameCard = actionPlan?.some((action) => action.primary);
                      const actions = actionPlan?.map((action) => ({
                        disabled: gameSimInProgress,
                        highlight: action.primary,
                        text:
                          action.kind === "watch"
                            ? i18n.t("dailySchedule.watchGameAction", {
                                ns: "ui",
                                defaultValue: "Watch game",
                              })
                            : i18n.t("dailySchedule.simGameAction", {
                                ns: "ui",
                                defaultValue: "Sim game",
                              }),
                        onClick: () =>
                          toWorker(
                            "actions",
                            action.kind === "watch" ? "liveGame" : "simGame",
                            game.gid,
                          ),
                      }));

                      let playersUpcoming: [any, any] | undefined;
                      const x0 = topPlayers.playersByTid[game.teams[0].tid];
                      const x1 = topPlayers.playersByTid[game.teams[1].tid];

                      // Undefined for ASG
                      if (x0 && x1) {
                        playersUpcoming = [x0[0], x1[0]];
                      }

                      return (
                        <div
                          className="flex-grow-1 daily-schedule-game-card"
                          data-daily-schedule-game-card=""
                          data-gid={game.gid}
                          data-primary-game={primaryGameCard ? "true" : "false"}
                          data-user-team-game={userTeamGame ? "true" : "false"}
                          key={game.gid}
                          style={{ maxWidth: 510 }}
                        >
                          <ScoreBox
                            scheduleLayout
                            game={{
                              // Leave out forceTie, since ScoreBox wants the value for finished games
                              finals: game.finals,
                              gid: game.gid,
                              season: game.season,
                              teams: game.teams,
                            }}
                            playersUpcoming={playersUpcoming}
                            actions={actions}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
              {secondaryUpcomingGames.length > 1 ? (
                <div data-daily-schedule-other-games-toggle="">
                  <MobileListToggle
                    expanded={otherGamesExpanded}
                    onClick={() => setOtherGamesExpanded((previous) => !previous)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {completed.length > 0 ? (
            <>
              <h2 className="mt-3">
                {i18n.t("dailySchedule.matchdayCompleted", {
                  ns: "ui",
                  defaultValue: "Games completed",
                })}
              </h2>
              <div
                className="daily-schedule-completed-list d-grid gap-2"
                data-daily-schedule-completed-list=""
              >
                {completedSorted.map((game) => (
                  <CompletedGameRow
                    game={game}
                    key={game.gid}
                    returnHref={helpers.leagueUrl(["daily_schedule", season, day])}
                    returnLabel={dailyScheduleTitle}
                    teamInfoCache={teamInfoCache}
                    userTid={userTid}
                  />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default DailySchedule;
